import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import Typography from "@mui/material/Typography";
import {
    getCategoryTemplate,
    addCategoryTemplateShowGroupKeys,
    addCategoryTemplateGroupKey,
    setCategoryTemplateShowGroupKeys,
} from "../../../actions/categoryTemplateActions";
import {
    initializeCategoryEditorCreate,
    initializeCategoryEditorUpdate,
    updateCategoryTemplate,
    clearCategoryEditor,
    validateCategoryNameAndQualifierUnique,
    addCategoryGroup,
    addCategoryShowGroupKeys,
    setCategoryShowGroupKeys
} from "../../../actions/categoryEditorActions";
import {
    getGroupTemplate,
    createGroupTemplate,
    saveGroupTemplate
} from "../../../actions/groupTemplateActions";
import {saveQuestionTemplate} from "../../../actions/questionTemplateActions";
import {expanderClear} from "../../../actions/expanderActions";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import GroupEditor from "../groups/GroupEditorHook";
import GroupViewerExpandableHook from "../groups/GroupViewerExpandableHook";
import ArgoMetadataEditorHook from "../../common/ArgoMetadataEditorHook";
import ArgoColorInputHook from "../../common/ArgoColorInputHook";
import ArgoSelectExistingHook from "../../common/ArgoSelectExistingHook";
import ArgoSelectPortalTextDisplayTypeHook from "../../common/ArgoSelectPortalTextDisplayTypeHook";
import ArgoTextDisplayCustomHook from "../../common/ArgoTextDisplayCustomHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import ArgoCheckboxHook from "../../common/ArgoCheckboxHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextEntryWithCustomOverrideHook from "../../common/ArgoTextEntryWithCustomOverrideHook";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import QuestionEditor from "../questions/QuestionEditorHook";
import * as validate from "../../../utilities/validate";
import * as metadataUtils from "../../../utilities/metadata";
import * as metadata from "../../../utilities/metadata";
import * as metadataTemplateTypes from "../../../constants/metadataTemplateTypes";
import * as TEMPLATE from "../../../constants/templateConstants";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities"
import {notificationOpen} from "../../../actions/notificationActions";
import {templateSaveSucceeded} from "../../../actions/templateSaveActions";
import useDeferredTemplateSave from "../../../hooks/useDeferredTemplateSave";
import {createEditorOrigin, EDITOR_TYPES} from "../../../utilities/editorInstanceOwnership";


const CategoryEditorHook = (props) => {
    const dispatch = useDispatch();

    const template = useSelector(state => state.categoryEditor.template);
    const groupKeys = useSelector(state => state.categoryEditor.groupKeys);
    const options = useSelector(state => state.categoryEditor.options);
    const categoryTemplate = useSelector(state => state.categoryTemplate);
    const templateOriginal = useSelector(state => state.categoryTemplate[props.templateKey]);
    const groupTemplateList = useSelector(state => state.groupTemplateList);
    const groupTemplate = useSelector(state => state.groupTemplate);
    const questionTemplates = useSelector(state => state.questionTemplate);
    const editorInstanceId = useSelector(state => state.categoryEditor.instanceId);

    const [localState, setLocalState] = useState(() => ({
        groupTemplateSearch: "",
        openGroupReview: false,
        selectedGroupInfoId: null,
        selectedGroupInfoKey: "",
        selectedGroupInfoTemplate: null,
        hidePortalPreview: false,
        showCategoryBackButton: false,
        openQuestionReview: false,
        selectedQuestionInfoId: null,
        selectedQuestionInfoKey: "",
        selectedQuestionInfoTemplate: null,
        isThisAnExistingGroup: "0",
        nameErrorText: " ",
        metadataErrorText: "",
        isLoading: false,
        groupList: [],
        editGroupKey: null,
        customCategoryObject: portalShared.getCustomCategoryObjectDefault(),
        nameDisplay: {...metadata.defaultCustomMetadataObj},
        descDisplay: {...metadata.defaultCustomMetadataObj},
        scriptDisplay: {...metadata.defaultCustomMetadataObj},
        toolbarErrorText: "",
        openGroupEditor: false,
        groupTemplate: {},
        openQuestionEditor: false,
        questionTemplate: {},
        metadataSet: false,
        initialScroll: false
    }));

    useEffect(() => {
        dispatch(addDrawerNameToStack(props.createMode ? "Create" : "Edit", "Category"));

        if (!props.createMode) {
            dispatch(getCategoryTemplate(props.templateKey)); // Force re-load in case stored template is stale
        }

        // componentWillUnmount equivalent
        return () => {
            dispatch(expanderClear("CategoryEditor"));
            dispatch(clearCategoryEditor());
            dispatch(removeDrawerNameToStack())
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    const localStateRef = useRef(localState);
    useEffect(() => {
        localStateRef.current = localState;
    }, [localState]);


    useEffect(() => {

        let tempCategoryTemplate = props.createMode ? template : templateOriginal;

        // If the category has groups
        if (tempCategoryTemplate && tempCategoryTemplate.groupKeys.length) {

            // Check to see if the groups are loaded
            for (let i = 0; i < tempCategoryTemplate.groupKeys.length; i++) {
                if (groupTemplate.hasOwnProperty(tempCategoryTemplate.groupKeys[i])) {
                    // need to check showGroupKeys
                    let tempGroupTemplate = groupTemplate[tempCategoryTemplate.groupKeys[i]];
                    for (let ii = 0; ii < tempGroupTemplate.showGroupKeys.length; ii++) {

                        // If group has showGroupKeys then make sure groups are loaded and added to category.showGroupKeys
                        if (!tempCategoryTemplate.showGroupKeys.includes(tempGroupTemplate.showGroupKeys[ii])) {
                            // Add Key to category.showGroupKeys
                            if (props.createMode) {
                                dispatch(addCategoryShowGroupKeys(tempCategoryTemplate, [tempGroupTemplate.showGroupKeys[ii]]));

                            } else {
                                dispatch(addCategoryTemplateShowGroupKeys(tempCategoryTemplate, [tempGroupTemplate.showGroupKeys[ii]]));
                            }
                        }

                        // Check to see if group is loaded
                        if (!groupTemplate.hasOwnProperty(tempGroupTemplate.showGroupKeys[ii])) {
                            dispatch(getGroupTemplate(tempGroupTemplate.showGroupKeys[ii])); // Show Group needs to be loaded for expander to display
                        }

                    }
                } else {
                    // Group not loaded so get it
                    dispatch(getGroupTemplate(tempCategoryTemplate.groupKeys[i])); // get the group to check for showGroupKeys
                }
            }
        }


    }, [template, templateOriginal, groupTemplate, groupTemplateList, props.createMode, dispatch]);



    const mergeLocalState = useCallback((stateObj) => {
        setLocalState(prev => {
            const next = {...prev, ...stateObj};
            localStateRef.current = next;
            return next;
        });
    }, []);

    const pageInit = useCallback((templateOriginalArg) => {
        if (!templateOriginalArg || !templateOriginalArg.metadata) return;

        let tempCustomCategoryObject = portalShared.cloneObj(localStateRef.current.customCategoryObject);

        const metadataObj = templateOriginalArg.metadata;
        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_DDO_CAT_FORMAT)) {
            tempCustomCategoryObject = portalShared.setCustomCategoryObject(
                JSON.parse(templateOriginalArg.metadata[portalShared.CUSTOM_DDO_CAT_FORMAT])
            );
        }

        const tempNameDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(
            metadataObj,
            portalShared.CUSTOM_DDO_NAME,
            tempCustomCategoryObject.titlebar
        );
        const tempDescDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(
            metadataObj,
            portalShared.CUSTOM_DDO_DESC,
            tempCustomCategoryObject.titlebar
        );

        mergeLocalState({
            customCategoryObject: tempCustomCategoryObject,
            nameDisplay: metadata.setCustomMetadataObj(tempNameDisplayObj),
            descDisplay: metadata.setCustomMetadataObj(tempDescDisplayObj)
        });
    }, [mergeLocalState]);

    // setTitleObject = (metadataTag, metadataObj) => {
    //     let titleObj = {...metadata.defaultCustomMetadataObj};
    //     // If the title Left/Center/Right has a metadata tag then can be custom with object value, or a string "hide"
    //     if (metadataObj.hasOwnProperty(metadataTag)) {
    //         titleObj.metadataValue = metadataObj[metadataTag];
    //         titleObj.metadataValueObj = JSON.parse(titleObj.metadataValue);
    //         titleObj.selected = titleObj.metadataValueObj.selected;
    //     }
    //     return titleObj;
    // }

    // setTextObject = (obj) => {
    //     return {
    //         selected: obj.selected,
    //         metadataValue: obj.metadataValue,
    //         metadataValueObj: obj.metadataValueObj
    //     };
    // }

    const setMetadataErrorText = useCallback((metadataErrorText) => {
        mergeLocalState({metadataErrorText: metadataErrorText, metadataSet: false});
    }, [mergeLocalState]);

    const metadataChanged = useCallback((updatedTemplate, depth) => {
        mergeLocalState({metadataSet: true});
        dispatch(updateCategoryTemplate(updatedTemplate, depth));
    }, [dispatch, mergeLocalState]);

    const addSelectedGroup = useCallback((groupTemplateKey) => {

        if (!groupKeys.includes(groupTemplateKey.templateKey)) {
            dispatch(getGroupTemplate(groupTemplateKey.templateKey)); // get the group to check for showGroupKeys

            dispatch(addCategoryGroup(groupTemplateKey.templateKey));
            if (!props.createMode) {
                dispatch(addCategoryTemplateGroupKey(templateOriginal, groupTemplateKey.templateKey));
            }

        }
        mergeLocalState({groupTemplateSearch: ""});
    }, [dispatch, groupKeys, mergeLocalState, templateOriginal, props.createMode]);

    const removeGroup = (groupTemplateKey) => {

        let categoryTemplate = props.createMode ? template : templateOriginal;
        let showGroupKeys = [];

        // Loop through all groups still assigned and build a list of showGroupKeys
        for (let i = 0; i < template.groupKeys.length; i++) {
            if (template.groupKeys[i] !== groupTemplateKey) {
                // From any remaining groups assigned add any showGroupKeys
                for (let ii = 0; ii < groupTemplate[template.groupKeys[i]].showGroupKeys.length; ii++) {
                    showGroupKeys.push(groupTemplate[template.groupKeys[i]].showGroupKeys[ii]);
                }
            }
        }

        if (props.createMode) {
            dispatch(setCategoryShowGroupKeys(groupTemplateKey, showGroupKeys));
        } else {
            dispatch(setCategoryTemplateShowGroupKeys(categoryTemplate, groupTemplateKey, showGroupKeys));
        }

        // if (!groupKeys.includes(groupTemplateKey.templateKey)) {
        //     dispatch(getGroupTemplate(groupTemplateKey.templateKey)); // get the group to check for showGroupKeys
        //
        //     dispatch(addCategoryGroup(groupTemplateKey.templateKey));
        //     if (!props.createMode) {
        //         dispatch(addCategoryTemplateGroupKey(templateOriginal, groupTemplateKey.templateKey));
        //     }
        //
        // }
        // mergeLocalState({groupTemplateSearch: ""});
    };

    const setMetadataValueFor = useCallback((customCategoryObjectArg) => {
        let updatedTemplate = portalShared.cloneObj(template);
        let metadataObj = {...updatedTemplate.metadata};

        if ((customCategoryObjectArg.portalPageBreak) || (customCategoryObjectArg.titlebar)) {
            metadataObj[portalShared.CUSTOM_DDO_CAT_FORMAT] = JSON.stringify(customCategoryObjectArg);
        }
        else {
            delete metadataObj[portalShared.CUSTOM_DDO_CAT_FORMAT];
        }

        updatedTemplate.metadata = metadataObj;
        dispatch(updateCategoryTemplate(updatedTemplate));
    }, [dispatch, template]);

    const onCheckboxChange = useCallback((event, fieldName) => {
        let tempCustomCategoryObject = portalShared.cloneObj(localStateRef.current.customCategoryObject);
        tempCustomCategoryObject[fieldName] = event.target.checked;

        if ((fieldName === "titlebar") && (event.target.checked)) {
            portalShared.setCustomCategoryObjectTitleBarDefaults(tempCustomCategoryObject);
            mergeLocalState({
                customCategoryObject: tempCustomCategoryObject,
                nameDisplay: {selected: "hide", metadataValue: "", metadataValueObj: "hide"},
                descDisplay: {selected: "hide", metadataValue: "", metadataValueObj: "hide"}
            });
            setMetadataValueFor(tempCustomCategoryObject);
        }
        else if ((fieldName === "titlebar") && (!event.target.checked)) {
            tempCustomCategoryObject.colorObj = null;
            tempCustomCategoryObject.leftTitleCustomTextObj = null;
            tempCustomCategoryObject.centerTitleCustomTextObj = null;
            tempCustomCategoryObject.rightTitleCustomTextObj = null;

            mergeLocalState({
                customCategoryObject: tempCustomCategoryObject,
                nameDisplay: {selected: "default", metadataValue: "", metadataValueObj: "default"},
                descDisplay: {selected: "default", metadataValue: "", metadataValueObj: "default"}
            });
            setMetadataValueFor(tempCustomCategoryObject);
        }
        else {
            mergeLocalState({customCategoryObject: tempCustomCategoryObject});
            setMetadataValueFor(tempCustomCategoryObject);
        }
    }, [mergeLocalState, setMetadataValueFor]);

    const onTextChange = useCallback((event, fieldName) => {
        let value = event.target.value;

        if ((fieldName === "nameQualifier") && (value === "")) {
            value = null;
        }

        let updatedTemplate = {...template};
        updatedTemplate[fieldName] = value;

        dispatch(updateCategoryTemplate(updatedTemplate));

        if (fieldName === "name" || fieldName === "nameQualifier")
        {
            dispatch(validateCategoryNameAndQualifierUnique(updatedTemplate.name, updatedTemplate.nameQualifier));
        }
    }, [dispatch, template]);

    const onNumberChange = useCallback((event) => {
        event.preventDefault(); // Stops scroll wheel from double-clicks on single-click

        let value = event.target.value;

        let customCategoryObject = portalShared.cloneObj(localStateRef.current.customCategoryObject);
        customCategoryObject.height = value;
        mergeLocalState({customCategoryObject: customCategoryObject});
        setMetadataValueFor(customCategoryObject);
    }, [mergeLocalState, setMetadataValueFor]);

    const saveCategory = useCallback(() => {

        let templateToSave;

        if (template.hasOwnProperty(props.templateKey)) {
            templateToSave = {
                ...template[props.templateKey],
                groupKeys: [...groupKeys]
            };
        } else {
            templateToSave = {
                ...template,
                groupKeys: [...groupKeys]
            };
        }

        // If edit is being called from category expander, after QuestionnaireEditor state is set it calls saveCategoryTemplate
        if (props.categoryExpanderEdit) {
            return props.categoryExpanderSetStateCallback({categoryTemplate: templateToSave}, true, TEMPLATE.TYPE.CATEGORY);
        }

        return props.save(templateToSave);
    }, [groupKeys, props, template]);

    const notifyDeferredSave = useCallback(() => {
        dispatch(notificationOpen("Waiting for the new template to finish saving…"));
    }, [dispatch]);
    const saveCategoryWhenReady = useDeferredTemplateSave(saveCategory, notifyDeferredSave);

    const handleToolbarButtonClick = useCallback((event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "CANCEL":
                props.cancel();
                break;
            case "SAVE":
                saveCategoryWhenReady();
                break;
            case "BACK":
                mergeLocalState({openGroupReview: false, openQuestionReview: false});
                break;
            // no default
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.cancel, mergeLocalState, saveCategoryWhenReady]);

    const onChangeIsThisAnExistingGroup = useCallback(() => {

        let newState = {
            isThisAnExistingGroup: "1",
            groupTemplateSearch: localStateRef.current.groupTemplateSearch
        };

        mergeLocalState(newState);
    }, [mergeLocalState]);

    const setColorForPortalTitleBar = useCallback((colorObj) => {
        let customCategoryObject = portalShared.cloneObj(localStateRef.current.customCategoryObject);
        customCategoryObject.colorObj = colorObj["colorObj"];
        mergeLocalState({customCategoryObject: customCategoryObject});
        setMetadataValueFor(customCategoryObject);
    }, [mergeLocalState, setMetadataValueFor]);

    const setMetadata = useCallback((customCategoryObjectArg, metadataTag) => {
        let updatedTemplate = {...template};
        let metadataObj = {...updatedTemplate.metadata};

        metadataObj[metadataTag] = JSON.stringify(customCategoryObjectArg);
        updatedTemplate.metadata = metadataObj;

        dispatch(updateCategoryTemplate(updatedTemplate));
    }, [dispatch, template]);

    const setCustomMetadataCallbackAlt = useCallback((fieldTextObjName, templateArg, updateTemplateFunction, setStateCallbackFunction, fieldName, selected, metadataTag, metadataValueObj) => {
        let customCategoryObject = JSON.parse(templateArg.metadata[metadataTag]);

        customCategoryObject[fieldTextObjName].format = selected;

        if (fieldName.includes(".format")) {
            if (selected === "default" || selected === "custom") {
                customCategoryObject[fieldTextObjName].title = "";
            }
            else if (selected.includes("pageNum")) {
                customCategoryObject[fieldTextObjName].title = portalShared.METADATA_VALUE_PAGENUM;
            }
            else if (selected.includes("pageofNum")) {
                customCategoryObject[fieldTextObjName].title = portalShared.METADATA_VALUE_PAGEOFNUM;
            }
        }
        else {
            customCategoryObject[fieldTextObjName] = metadataValueObj;
        }

        mergeLocalState({customCategoryObject: customCategoryObject});
        setMetadata(customCategoryObject, metadataTag);
    }, [mergeLocalState, setMetadata]);

    // -----------------------------------------------------------------------------------------------------------------
    // GROUP EDITOR FUNCTIONS ------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const groupEditorCreate = useCallback((groupTemplateArg) => {
        const saveResult = localStateRef.current.editGroupKey
            ? dispatch(saveGroupTemplate(groupTemplateArg, true))
            : dispatch(createGroupTemplate(
                groupTemplateArg,
                createEditorOrigin(EDITOR_TYPES.CATEGORY, editorInstanceId)
            ));

        return saveResult.then(result => {
            if (templateSaveSucceeded(result)) {
                mergeLocalState({isThisAnExistingGroup: "0", groupTemplateSearch: "", editGroupKey: null});
            }
            return result;
        });
    }, [dispatch, editorInstanceId, mergeLocalState]);

    const groupEditorClose = useCallback(() => {
        mergeLocalState({isThisAnExistingGroup: "0", groupTemplateSearch: "", editGroupKey: null});
    }, [mergeLocalState]);

    const groupEditorSave = useCallback((groupTemplateArg) => {
        return dispatch(saveGroupTemplate(groupTemplateArg ?? localStateRef.current.groupTemplate)).then(result => {
            if (templateSaveSucceeded(result)) {
                mergeLocalState({openGroupEditor: false});
            }
            return result;
        });
    }, [dispatch, mergeLocalState]);

    const groupEditorCancel = useCallback(() => {
        mergeLocalState({openGroupEditor: false});
    }, [mergeLocalState]);

    // -----------------------------------------------------------------------------------------------------------------
    // QUESTION EDITOR FUNCTIONS ------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const questionEditorSave = useCallback((questionTemplateArg) => {
        return dispatch(saveQuestionTemplate(questionTemplateArg ?? localStateRef.current.questionTemplate)).then(result => {
            if (templateSaveSucceeded(result)) {
                mergeLocalState({openQuestionEditor: false});
            }
            return result;
        });
    }, [dispatch, mergeLocalState]);

    const questionEditorCancel = useCallback(() => {
        mergeLocalState({openQuestionEditor: false});
    }, [mergeLocalState]);

    const setStateCallback = useCallback((stateObj, saveAfter = false, templateType) => {
        setLocalState(prev => {
            const next = {...prev, ...stateObj};
            localStateRef.current = next;
            return next;
        });

        if ((templateType === TEMPLATE.TYPE.GROUP) && saveAfter) {
            const next = {...localStateRef.current, ...stateObj};
            return groupEditorSave(next.groupTemplate);
        }
        if ((templateType === TEMPLATE.TYPE.QUESTION) && saveAfter) {
            const next = {...localStateRef.current, ...stateObj};
            return questionEditorSave(next.questionTemplate);
        }
        return undefined;
    }, [groupEditorSave, questionEditorSave]);

    // -----------------------------------------
    // Lifecycle conversions
    // -----------------------------------------

    useEffect(() => {
        if (props.templateKey || props.templateId) {
            const cached = categoryTemplate[props.templateKey];
            if (cached && (!props.templateId || (cached.id === props.templateId))) {
                dispatch(initializeCategoryEditorUpdate(cached));
            }
            else if (props.templateKey) {
                mergeLocalState({isLoading: true});
                dispatch(getCategoryTemplate(props.templateKey, props.templateId));
            }
        }
        else {
            let templateArg = {
                ...(props.createInitialTemplate || {})
            };
            dispatch(initializeCategoryEditorCreate(templateArg));
        }

        if (templateOriginal) {
            pageInit(templateOriginal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.templateKey, props.templateId]);


    useEffect(() => {
        // Get showGroupKeys templates
        if (template.hasOwnProperty("showGroupKeys") && template.showGroupKeys.length) {
            for (let i = 0; i < template.showGroupKeys.length; i++) {
                dispatch(getGroupTemplate(template.showGroupKeys[i]));
            }
        }

    }, [template, dispatch]);


    useEffect(() => {
        if (!localStateRef.current.initialScroll) {
            const scrollTo = (props.scrollTo === "") ? "CategoryEditor-height-div" : props.scrollTo;
            const page = document.getElementById(scrollTo);
            if (page) {
                page.scrollIntoView({block: 'end', behavior: 'smooth'});
                mergeLocalState({initialScroll: true});
            }
        }
    }, [mergeLocalState, props.scrollTo, template]);

    useEffect(() => {
        const curr = localStateRef.current;

        const isTemplateLoaded = (templateOriginal && templateOriginal.templateKey === props.templateKey
            && template?.templateKey !== props.templateKey);

        const editTemplateReady = template?.id !== props.templateId &&
            (categoryTemplate[props.templateKey] && categoryTemplate[props.templateKey].id === props.templateId);

        if (!curr.metadataSet) {
            if (isTemplateLoaded && editTemplateReady) {
                mergeLocalState({isLoading: false, groupList: groupTemplateList});
                dispatch(initializeCategoryEditorUpdate(templateOriginal));
                pageInit(templateOriginal);
            }
            else {
                mergeLocalState({groupList: groupTemplateList});
            }
        }
    }, [categoryTemplate, dispatch, groupTemplateList, mergeLocalState, pageInit, props.templateId, props.templateKey, template, templateOriginal]);

    useEffect(() => {
        if (groupKeys && groupKeys.length > 0) {
            groupKeys.forEach((templateKey) => {
                if (!groupTemplate[templateKey]) {
                    dispatch(getGroupTemplate(templateKey));
                }
            });
        }
    }, [dispatch, groupKeys, groupTemplate]);

    useEffect(() => {
        const nameErrorText = validate.templateNameAndQualifierUnique({template, options}, "Category Name");
        const metadataErrorText = localStateRef.current.metadataErrorText;

        let toolbarErrorText = "";
        if (nameErrorText !== " ") {
            toolbarErrorText = nameErrorText;
        }
        if (metadataErrorText !== "") {
            toolbarErrorText = toolbarErrorText === "" ? metadataErrorText : toolbarErrorText + ",  " + metadataErrorText;
        }

        mergeLocalState({nameErrorText, toolbarErrorText});
    }, [mergeLocalState, options, template]);

    const metadataObj = useMemo(
        () => (template && template.metadata) ? template.metadata : {},
        [template]
    );

    const propsObj = useMemo(() => ({
        fileName: "CategoryEditor",
        onTextChange: onTextChange,
        templateType: TEMPLATE.TYPE.CATEGORY,
        template: template,
        metadata: metadataObj,
        templateUpdateFunction: (updatedTemplate, optionsArg) => dispatch(updateCategoryTemplate(updatedTemplate, optionsArg)),
        setStateCallbackFunction: setStateCallback,
        setCustomMetadataFunction: metadataUtils.setCustomMetadata,
        setCustomMetadataFunctionAlt: setCustomMetadataCallbackAlt,
        depth: 0
    }), [dispatch, metadataObj, onTextChange, setCustomMetadataCallbackAlt, setStateCallback, template]);

    const categoryTemplateClone = useMemo(() => {
        let clone = portalShared.cloneObj(template);
        // if (!props.createMode && templateOriginal) {
        //     clone = portalShared.cloneObj(templateOriginal);
        // }
        clone.groupKeys = groupKeys;
        return clone;
    }, [groupKeys, template]);

    if (localState.isLoading) {
        // TODO place a loading screen here
        return null;
    }

    if (localState.openGroupReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.GROUP}
            templateId={localState.selectedGroupInfoId}
            templateKey={localState.selectedGroupInfoKey}
            selectedTemplate={localState.selectedGroupInfoTemplate}
            selectedTemplateIsGraph={false}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }

    if (localState.openQuestionReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.QUESTION}
            templateId={localState.selectedQuestionInfoId}
            templateKey={localState.selectedQuestionInfoKey}
            selectedTemplate={localState.selectedQuestionInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }

    if (localState.isThisAnExistingGroup === "1") {
        let templateArg = {
            name: localState.groupTemplateSearch
        };

        return <GroupEditor title="Create Group"
                            createMode={true}
                            save={groupEditorCreate}
                            cancel={groupEditorClose}
                            createInitialTemplate={templateArg}
                            templateKey={localState.editGroupKey}
                            width={props.width}
        />;
    }

    // Edit from group expander
    if (localState.openGroupEditor) {

        return <GroupEditor title="Edit Group"
                            save={groupEditorSave}
                            cancel={groupEditorCancel}
                            templateKey={localState.groupTemplate.templateKey}
                            templateId={localState.groupTemplate.id}
                            filter={props.filter}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            groupExpanderEdit={true}
                            groupExpanderSetStateCallback={setStateCallback}
                            width={props.width}
                            scrollTo="CategoryEditor-container-outer-div"
        />;
    }

    // Edit from question expander
    if (localState.openQuestionEditor) {

        return <QuestionEditor title="Edit Question"
                               save={questionEditorSave}
                               cancel={questionEditorCancel}
                               templateKey={localState.questionTemplate.templateKey}
                               templateId={localState.questionTemplate.id}
                               filter={props.filter}
                               templateMode={TEMPLATE.MODE.CURRENT}
                               questionDepth={0}
                               questionExpanderEdit={true}
                               questionExpanderSetStateCallback={setStateCallback}
                               width={props.width}
                               scrollTo="CategoryEditor-container-outer-div"
        />;
    }

    return (
        template && template.hasOwnProperty("templateKey") ?
            <div id="CategoryEditor-container-outer-div" style={{height: "100%"}}>

                <ArgoToolbarHook
                    fileName="CategoryEditor"
                    fieldName="save-or-cancel"
                    toolbarErrorText={localState.toolbarErrorText}
                    isValidating={options.isValidating}
                    onClick={handleToolbarButtonClick}
                    width={props.width}
                />

                <div id="CategoryEditor-height-div" style={{height: "50px"}}></div>

                <div>&nbsp;</div>

                <div id="CategoryEditor-page-div" style={{height: "100%", width: "100%"}}>

                    <div id="CategoryEditor-container-inner-div"
                         style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px"}}>

                        <div id="CategoryEditor-title-div">
                            {props.title === "" ? "" :
                                <div>
                                    <div>&nbsp;</div>
                                    <Typography style={{fontWeight: "bold"}}>{props.title}</Typography>
                                    <div>&nbsp;</div>
                                </div>
                            }
                        </div>

                        <div id="CategoryEditor-pagebreak-container-div"
                             style={{display: "flex", flexDirection: "row", alignItems: "center", width: "100%"}}>

                            <ArgoCheckboxHook
                                fileName="CategoryEditor"
                                fieldName="portalPageBreak"
                                label="Portal page break"
                                onChange={onCheckboxChange}
                                checked={localState.customCategoryObject.portalPageBreak}
                            />

                            <div style={{width: "20px"}}></div>

                            <ArgoCheckboxHook
                                fileName="CategoryEditor"
                                fieldName="titlebar"
                                label="Portal title bar"
                                onChange={onCheckboxChange}
                                checked={localState.customCategoryObject.titlebar}
                                width="150px"
                            />

                            <div style={{width: "20px"}}></div>

                            {localState.customCategoryObject.titlebar ?
                                <div id="CategoryTitleBar-div" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                                    <ArgoTextFieldHook
                                        fileName="CategoryEditor"
                                        fieldName="height"
                                        index={0}
                                        type="number"
                                        label="Height"
                                        value={localState.customCategoryObject.height}
                                        onChange={onNumberChange}
                                        width={60}
                                    />

                                    <div style={{width: "20px"}}>&nbsp;</div>

                                    <ArgoColorInputHook mode="title"
                                                       colorLabel={"Title Bar Color"}
                                                       propsFieldName={"customCategoryObject"}
                                                       initToObj={localState.customCategoryObject.colorObj}
                                                       opacityFlag={true}
                                                       largeHexEntry={false}
                                                       setColor={setColorForPortalTitleBar}
                                                       height={'17px'}
                                    /></div> : ""
                            }
                        </div>

                        {localState.customCategoryObject.titlebar ?
                            <div id="CategoryEditor-titles-div" style={{width: "100%", backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX}}>

                                <div id="CategoryEditor-title-spacer-01-div" style={{height: "10px"}}/>

                                <div style={{display: "flex", alignItems: "left", width: "100%"}}>

                                    <div style={{width: "20px"}}></div>

                                    <ArgoSelectPortalTextDisplayTypeHook
                                        templateType={TEMPLATE.TYPE.CATEGORY}
                                        textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_LEFT}
                                        fieldName="leftTitleCustomTextObj.format"
                                        fieldTextObjName="leftTitleCustomTextObj"
                                        initTo={localState.customCategoryObject.leftTitleCustomTextObj.format}
                                        hideOption={false}
                                        labelText={"Left Title"}
                                        useMetadataPropsObjAltFunction={true}
                                        metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                    />

                                    {(localState.customCategoryObject.leftTitleCustomTextObj.format === "custom" ||
                                        localState.customCategoryObject.leftTitleCustomTextObj.format.includes("pageNum") ||
                                        localState.customCategoryObject.leftTitleCustomTextObj.format.includes("pageofNum")) ?
                                        <ArgoTextDisplayCustomHook
                                            templateType={TEMPLATE.TYPE.CATEGORY}
                                            textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_LEFT}
                                            fieldName="leftTitleCustomTextObj"
                                            fieldTextObjName="leftTitleCustomTextObj"
                                            initToObj={localState.customCategoryObject.leftTitleCustomTextObj}
                                            showDisplay={false}
                                            showTitleEntry={true}
                                            paddingTop={"0px"}
                                            useMetadataPropsObjAltFunction={true}
                                            metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                        />
                                        :
                                        <div></div>
                                    }
                                </div>

                                <div id="CategoryEditor-title-spacer-02-div" style={{height: "10px"}}/>

                                <div style={{display: "flex", alignItems: "left", width: "100%"}}>

                                    <div style={{width: "20px"}}></div>

                                    <ArgoSelectPortalTextDisplayTypeHook
                                        templateType={TEMPLATE.TYPE.CATEGORY}
                                        textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_CENTER}
                                        fieldName="centerTitleCustomTextObj.format"
                                        fieldTextObjName="centerTitleCustomTextObj"
                                        initTo={localState.customCategoryObject.centerTitleCustomTextObj.format}
                                        hideOption={false}
                                        labelText={"Center Title"}
                                        useMetadataPropsObjAltFunction={true}
                                        metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                    />

                                    {(localState.customCategoryObject.centerTitleCustomTextObj.format === "custom" ||
                                        localState.customCategoryObject.centerTitleCustomTextObj.format.includes("pageNum") ||
                                        localState.customCategoryObject.centerTitleCustomTextObj.format.includes("pageofNum")) ?
                                        <ArgoTextDisplayCustomHook
                                            templateType={TEMPLATE.TYPE.CATEGORY}
                                            textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_CENTER}
                                            fieldName="centerTitleCustomTextObj"
                                            fieldTextObjName="centerTitleCustomTextObj"
                                            initToObj={localState.customCategoryObject.centerTitleCustomTextObj}
                                            showDisplay={false}
                                            showTitleEntry={true}
                                            paddingTop={"0px"}
                                            useMetadataPropsObjAltFunction={true}
                                            metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                        />
                                        :
                                        <div></div>
                                    }

                                </div>

                                <div id="CategoryEditor-title-spacer-03-div" style={{height: "10px"}}/>

                                <div style={{display: "flex", alignItems: "left", width: "100%"}}>

                                    <div style={{width: "20px"}}></div>

                                    <ArgoSelectPortalTextDisplayTypeHook
                                        templateType={TEMPLATE.TYPE.CATEGORY}
                                        textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_RIGHT}
                                        fieldName="rightTitleCustomTextObj.format"
                                        fieldTextObjName="rightTitleCustomTextObj"
                                        initTo={localState.customCategoryObject.rightTitleCustomTextObj.format}
                                        hideOption={false}
                                        labelText={"Right Title"}
                                        useMetadataPropsObjAltFunction={true}
                                        metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                    />

                                    {(localState.customCategoryObject.rightTitleCustomTextObj.format === "custom" ||
                                        localState.customCategoryObject.rightTitleCustomTextObj.format.includes("pageNum") ||
                                        localState.customCategoryObject.rightTitleCustomTextObj.format.includes("pageofNum")) ?
                                        <ArgoTextDisplayCustomHook
                                            templateType={TEMPLATE.TYPE.CATEGORY}
                                            textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.HEADER_RIGHT}
                                            fieldName="rightTitleCustomTextObj"
                                            fieldTextObjName="rightTitleCustomTextObj"
                                            initToObj={localState.customCategoryObject.rightTitleCustomTextObj}
                                            showDisplay={false}
                                            showTitleEntry={true}
                                            paddingTop={"0px"}
                                            useMetadataPropsObjAltFunction={true}
                                            metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_CAT_FORMAT}, propsObj)}
                                        />
                                        :
                                        <div></div>
                                    }

                                </div>

                            </div>
                            : ""}

                    </div>

                    <div id="CategoryEditor-break-between-titles-main-page">&nbsp;</div>

                    <div style={{justifyContent: "space-between"}}>

                        <div style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px"}}>

                            {props.showCreateToggle && props.showCreateToggle()}

                            <ArgoTextEntryWithCustomOverrideHook
                                templateType={TEMPLATE.TYPE.CATEGORY}
                                textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.NAME}
                                fieldName="name"
                                fieldTextObjName="nameDisplay"
                                label="Category Name"
                                labelCustom="Display (Name)"
                                value={template.name}
                                maxlength={255}
                                errorText={localState.nameErrorText}
                                initTo={localState.nameDisplay.selected}
                                initToObj={localState.nameDisplay.metadataValueObj}
                                hideOnly={localState.customCategoryObject.titlebar}
                                propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_NAME}, propsObj)}
                                autoFocus={true}
                            />

                            <div id="CategoryEditor-qualifier-container-div"
                                 style={{display: "flex", alignItems: "left", width: "100%"}}>

                                <ArgoTextFieldHook
                                    fileName="CategoryEditor"
                                    fieldName="nameQualifier"
                                    label="Name Qualifier"
                                    value={template.nameQualifier}
                                    maxLength={255}
                                    multiline={true}
                                    onChange={onTextChange}
                                    width="96%"
                                    divContainerHeight="52px"
                                />

                                <div style={{width: "83px"}}>&nbsp;</div>

                            </div>

                            <div>&nbsp;</div>

                            <ArgoTextEntryWithCustomOverrideHook
                                templateType={TEMPLATE.TYPE.CATEGORY}
                                textEntryType={TEMPLATE.TEXT_TYPE.CATEGORY.DESC}
                                fieldName="description"
                                fieldTextObjName="descDisplay"
                                label="Description/Instructions"
                                labelCustom="Display (Description)"
                                value={template.description}
                                maxlength={255}
                                initTo={localState.descDisplay.selected}
                                initToObj={localState.descDisplay.metadataValueObj}
                                hideOnly={localState.customCategoryObject.titlebar}
                                propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_DESC}, propsObj)}
                                showIndent={true}
                            />

                            {/*// ===========================================================================================================*/}
                            {/*// METADATA EDITOR */}
                            {/*// ===========================================================================================================*/}

                            {/*<div>&nbsp;</div>*/}

                            <div id="CategoryEditor-metadata-container-div" style={{width: "100%"}}>
                                <ArgoMetadataEditorHook
                                    entityType={metadataTemplateTypes.CategoryTemplate}
                                    template={template}
                                    // onChange={this.props.updateCategoryTemplate}
                                    onChangeCallback={metadataChanged}
                                    metadata={template.metadata}
                                    errorText={localState.metadataErrorText}
                                    setMetadataErrorText={setMetadataErrorText}
                                    hidden={true}
                                />
                            </div>

                            {/*// ===========================================================================================================*/}
                            {/*// ADD GROUP TO CATEGORY */}
                            {/*// ===========================================================================================================*/}

                            <ArgoSelectExistingHook
                                mode={TEMPLATE.TYPE.GROUP}
                                editorName="CategoryEditor"
                                paddingRight="0px"
                                onAddTemplate={addSelectedGroup}
                                width={140}
                                openEditorCallBack={onChangeIsThisAnExistingGroup}
                                createMode={props.createMode}
                                parentTemplate={template}
                            />

                            <div>&nbsp;</div>

                            {/*// ===========================================================================================================*/}
                            {/*// ASSIGNED GROUPS LIST */}
                            {/*// ===========================================================================================================*/}

                            <GroupViewerExpandableHook
                                parentName="CategoryEditor"
                                parentKey=""
                                categoryTemplate={template}
                                groupKeys={groupKeys}
                                setStateCallback={setStateCallback}
                                hidePortalPreview={true}
                                questionTemplate={questionTemplates}
                                removeGroupCallback={removeGroup}
                            />

                            {/*// ===========================================================================================================*/}
                            {/*// RELATED SHOW GROUPS LIST */}
                            {/*// ===========================================================================================================*/}

                            <GroupViewerExpandableHook
                                parentName="CategoryEditor"
                                parentKey=""
                                categoryTemplate={template}
                                groupKeys={props.createMode ? (template && template.hasOwnProperty("showGroupKeys") ? template.showGroupKeys : []) : ((templateOriginal && templateOriginal.hasOwnProperty("showGroupKeys")) ? templateOriginal.showGroupKeys : [])}
                                setStateCallback={setStateCallback}
                                hidePortalPreview={true}
                                questionTemplate={questionTemplates}
                                showIconButtonReorder={false}
                                //showIconButtonEdit={false}
                                showIconButtonRemove={false}
                                isShowGroups={true}
                            />

                            {/*// ===========================================================================================================*/}
                            {/*// PORTAL PREVIEW */}
                            {/*// ===========================================================================================================*/}

                            <div id="CategoryEditor-space-above-portal-preview"
                                 style={{height: "20px"}}>&nbsp;</div>

                            <ArgoPortalPreviewHook
                                calledBy="CategoryEditor"
                                createMode={props.createMode}
                                templateType={TEMPLATE.TYPE.CATEGORY}
                                template={categoryTemplateClone}
                            />

                        </div>
                    </div>
                </div>
            </div> : <div id="CategoryEditor-container-outer-div"/>
    );
};

CategoryEditorHook.defaultProps = {
    createMode: false,
    title: "",
    scrollTo: "",
    width: "100%"
};

CategoryEditorHook.propTypes = {
    scrollTo: PropTypes.string,
    createMode: PropTypes.bool,
    title: PropTypes.string,
    templateKey: PropTypes.string,
    templateId: PropTypes.number,
    cancel: PropTypes.func,
    save: PropTypes.func,
    showCreateToggle: PropTypes.func,
    createInitialTemplate: PropTypes.object,
    categoryExpanderEdit: PropTypes.bool,
    categoryExpanderSetStateCallback: PropTypes.func,
    width: PropTypes.string
};

export default CategoryEditorHook;
